import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  FormLabel,
  Fade,
  Chip,
  Tooltip,
} from "@mui/material";
import { MenuBook, Star } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
  normalizeMultilineText,
} from "../../Components/helpers/Helpers";
import fondoBlanco from "../../assets/fondoblanco.jpg";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";
import LoadPdf from "../../Biblioteca/Crud/LoadPdf/LoadPdf";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";

const Ver = () => {
  const { goEdit, goTable, buildEditUrl, buildTableUrl } = useLocationHelpers();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const associationId = searchParams.get("id");

  const [flag, setFlag] = useState(false);
  const [companyData, setCompanyData] = useState({});

  /* ---- multiple docs ---- */
  const [documents, setDocuments] = useState([]);
  const [principalDoc, setPrincipalDoc] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const normalizeDocs = (data) => {
    if (Array.isArray(data?.files_meta) && data.files_meta.length > 0) {
      return data.files_meta.map((doc) => ({
        original: doc.original,
        nombre_dinamico: doc.nombre_dinamico ?? "",
        display_name: doc.display_name ?? doc.original,
      }));
    }

    const files = data?.files ?? (data?.file ? [data.file] : []);
    return files.map((file) => ({
      original: file,
      nombre_dinamico: "",
      display_name: file,
    }));
  };

  /* ---- data (obs always last) ---- */
  const [asocData, setAsocData] = useState({
    asocName: "",
    asocCompany: "",
    asocGrupo: "",
    asocCif: "",
    asocPhone: "",
    asocEmail: "",
    asocContactname: "",
    asocAddress: "",
    asocPostalcode: "",
    asocProvince: "",
    asocObservaciones: "",
  });

  const checkDocument = async () => {
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/check-document`,
        { id: associationId },
      );
      const files = normalizeDocs(data);
      const principal =
        data?.principal_doc_meta?.original ??
        data?.principal_doc ??
        files?.[0]?.original ??
        null;
      setDocuments(files);
      setPrincipalDoc(principal);
      setSelectedDoc(principal ?? files?.[0]?.original ?? null);
    } catch {
      setDocuments([]);
      setPrincipalDoc(null);
      setSelectedDoc(null);
    }
  };

  const getData = async () => {
    setFlag(true);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/show`,
        { id: associationId },
      );

      const assoc = response.data?.association?.[0] ?? {};

      setCompanyData(assoc.company_relation ?? {});

      setAsocData({
        asocName: assoc.asoc_name ?? "",
        asocCompany: assoc.company_relation?.company_name ?? "",
        asocGrupo: assoc.group_relations?.grupo ?? "",
        asocCif: assoc.asoc_cif ?? "",
        asocPhone: assoc.asoc_phone ?? "",
        asocEmail: assoc.asoc_email ?? "",
        asocContactname: assoc.asoc_contactname ?? "",
        asocAddress: assoc.asoc_address ?? "",
        asocPostalcode: assoc.asoc_postalcode ?? "",
        asocProvince: assoc.asoc_province ?? "",
        asocObservaciones: assoc.asoc_observaciones ?? "", // LAST
      });

      await checkDocument();
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los datos",
        confirmButtonColor: "#1ab394",
      });
    } finally {
      setFlag(false);
    }
  };

  useEffect(() => {
    getData();
  }, [associationId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- delete association ---- */
  const handleDeleteAssociation = async () => {
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/check-document`,
        { id: associationId },
      );

      const hasFiles = (data.files?.length ?? 0) > 0 || !!data.file;

      if (hasFiles) {
        const confirmDoc = await Swal.fire({
          title: "Documento encontrado",
          text: "Existe documentacion relacionada a la asociacion. Si procede se eliminara.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          confirmButtonText: "Si, eliminar todo",
          cancelButtonText: "Cancelar",
          reverseButtons: true,
        });
        if (!confirmDoc.isConfirmed) return;
      } else {
        const confirm = await Swal.fire({
          title: "Eliminar asociacion",
          text: "Esta accion no se puede deshacer",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          reverseButtons: true,
        });
        if (!confirm.isConfirmed) return;
      }

      Swal.fire({
        title: hasFiles
          ? "Eliminando documento y asociacion..."
          : "Eliminando asociacion...",
        text: "Por favor espera",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      if (hasFiles) {
        await api.post(
          `${process.env.REACT_APP_API_URL}/associations/delete-document`,
          { id: associationId },
        );
        await new Promise((res) => setTimeout(res, 600));
      }

      await api.delete(
        `${process.env.REACT_APP_API_URL}/associations/destroy`,
        { data: { id: associationId } },
      );

      Swal.fire({
        icon: "success",
        title: "Eliminacion completada",
        text: hasFiles
          ? "Se elimino el documento y la asociacion correctamente"
          : "La asociacion se elimino correctamente",
        timer: 2000,
        showConfirmButton: false,
      });

      setTimeout(() => navigate("/asociaciones"), 1800);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo completar la eliminacion",
      });
    }
  };

  /* ---- label map (obs last = defined last in object) ---- */
  const labels = {
    asocName: "Nombre",
    asocCompany: "Empresa",
    asocGrupo: "Grupo",
    asocCif: "CIF",
    asocPhone: "Telefono",
    asocEmail: "Email",
    asocContactname: "Nombre de Contacto",
    asocAddress: "Direccion",
    asocPostalcode: "Codigo Postal",
    asocProvince: "Provincia",
    asocObservaciones: "Observaciones", // last
  };

  const ImageCell = () => {
    const imageUrl =
      companyData?.id && companyData?.company_logo
        ? `${process.env.REACT_APP_BACKEND_HOST}/storage/company/${companyData.id}/${companyData.company_logo}`
        : fondoBlanco;
    return <img src={imageUrl} alt="Company logo" className="image-views" />;
  };

  const hasData = Object.values(asocData).some(Boolean);

  /* ---- render ---- */
  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Asociaciones" subTitle="Ver" />

      <LoadingPage isLoading={flag} />

      <Fade in={!flag && hasData} timeout={500}>
        <Box
          sx={{
            maxWidth: 1200,
            margin: "auto",
            mt: 2,
            mx: { xs: "10px", xl: "auto" },
          }}
        >
          {!flag && (
            <Grid
              container
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                p: { xs: 2, md: 4 },
              }}
            >
              {/* Logo */}
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pb: { xs: 2, md: 0 },
                  pr: { md: 3 },
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 180, textAlign: "center" }}>
                  {ImageCell()}
                </Box>
              </Grid>

              {/* Datos */}
              <Grid item xs={12} md={8} lg={9}>
                <Grid container spacing={1}>
                  {Object.entries(asocData).map(([key, value]) => {
                    if (!labels[key]) return null;
                    if (key === "asocObservaciones") {
                      return (
                        <Grid item xs={12} key={key}>
                          <FormLabel
                            sx={{
                              fontWeight: 600,
                              color: "#555",
                              mb: 1,
                              display: "block",
                            }}
                          >
                            {labels[key]}
                          </FormLabel>
                          <Box
                            sx={{
                              backgroundColor: "#fafafa",
                              border: "1px solid #eee",
                              borderRadius: "10px",
                              p: 1.5,
                              fontSize: "14px",
                              lineHeight: 1.6,
                              minHeight: 60,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: normalizeMultilineText(value).replace(
                                /\n/g,
                                "<br>",
                              ),
                            }}
                          />
                        </Grid>
                      );
                    }
                    return (
                      <Grid item xs={12} sm={6} md={6} lg={4} key={key}>
                        <FormLabel
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "#666",
                          }}
                        >
                          {labels[key]}
                        </FormLabel>
                        <Box
                          sx={{
                            fontSize: "15px",
                            mt: "2px",
                            wordBreak: "break-word",
                          }}
                        >
                          {value || "-"}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              {/* Botones */}
              <Grid item xs={12}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} lg={4}>
                    <NavButton
                      fullWidth
                      to={buildEditUrl("asociaciones", associationId)}
                      onClick={() =>
                        goEdit("asociaciones", associationId, "Asociacion")
                      }
                      variant="contained"
                      color="success"
                    >
                      Editar
                    </NavButton>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <NavButton
                      fullWidth
                      to={buildTableUrl("asociaciones")}
                      onClick={() => goTable("asociaciones", "Asociaciones")}
                      variant="outlined"
                      color="error"
                    >
                      Tabla
                    </NavButton>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <Button
                      fullWidth
                      onClick={handleDeleteAssociation}
                      variant="contained"
                      color="error"
                    >
                      Eliminar Asociacion
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
                p: { xs: 2, md: 3 },
                mt: 4,
              }}
            >
              {/* Header + doc chips selector */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                }}
              >
                <MenuBook color="info" sx={{ mr: 0.5 }} />
                <Box sx={{ fontWeight: 600, fontSize: 15, mr: 1 }}>
                  Documentos adjuntos
                </Box>
                {documents.map((fileName) => {
                  const isPrincipal = fileName.original === principalDoc;
                  const isViewing = fileName.original === selectedDoc;
                  return (
                    <Tooltip
                      key={fileName.original}
                      title={
                        isPrincipal
                          ? "Documento principal"
                          : isViewing
                            ? "Visualizando"
                            : "Ver documento"
                      }
                    >
                      <Chip
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {isPrincipal && (
                              <Star sx={{ fontSize: 13, color: "#f5a623" }} />
                            )}
                            <span>{fileName.display_name}</span>
                          </Box>
                        }
                        onClick={() => setSelectedDoc(fileName.original)}
                        variant={isViewing ? "filled" : "outlined"}
                        color={isViewing ? "info" : "default"}
                        size="small"
                        sx={{
                          maxWidth: 260,
                          border: isPrincipal
                            ? "1.5px solid #f5a623"
                            : undefined,
                          bgcolor:
                            isPrincipal && !isViewing ? "#fffbf0" : undefined,
                          cursor: "pointer",
                          fontWeight: isViewing ? 600 : 400,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>

              {/* PDF viewer — key forces remount on doc change */}
              {selectedDoc && (
                <LoadPdf
                  key={selectedDoc}
                  bookName={selectedDoc}
                  bookId={associationId}
                  alternativeRute={"acuerdo/" + selectedDoc}
                />
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </div>
  );
};

export default Ver;
